import { useContext } from "react";

import { TenantContext } from "@nens/ranalib";
import { getWmsRequest } from "@nens/react-map-gl/utils";

import { RanaMapFullscreen } from "@/lib/rana-map";

import { getFiles, getId, getLayersOfType } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { CompleteFile } from "../../types/file-reference-with-file";
import { Layer, RasterLayer } from "../../types/publication";

import { useRasterStylingOptions } from "./hooks/use-raster-styling-options";

interface PublicationMapRasterLayersProps {
  getBeforeId: (layer: Layer) => string;
}

export function PublicationMapRasterLayers({ getBeforeId }: PublicationMapRasterLayersProps) {
  const layers = usePublicationStore(getLayersOfType("raster"));
  const files = usePublicationStore(getFiles);

  return (
    <>
      {layers.map((layer) => {
        const file = files.find((file) => file.path === layer.filePath);
        if (!file) return null;
        return (
          <SingleRasterLayer
            key={layer.id}
            beforeId={getBeforeId(layer)}
            layer={layer}
            file={file}
          />
        );
      })}
    </>
  );
}

interface SingleRasterLayerProps {
  file: CompleteFile;
  layer: RasterLayer;
  beforeId: string;
}

function SingleRasterLayer({ file, layer, beforeId }: SingleRasterLayerProps) {
  const stylingOptions = useRasterStylingOptions(file.file.descriptor, layer);
  const { tenant } = useContext(TenantContext);
  const publicationId = usePublicationStore(getId);

  if (stylingOptions === null) return null;

  const { bounds, wmsLayer, styles } = stylingOptions;

  // Rebuild WMS tile URL with proper utility function
  const wmsUrl = `/v1-alpha/tenants/${tenant}/publications/${publicationId}/file-descriptors/${file.file.descriptor.id}/wms`;
  const wmsTiles = getWmsRequest(wmsUrl, {
    layer: wmsLayer,
    styles,
    transparent: false,
  });

  return (
    <RanaMapFullscreen.Source bounds={bounds} type="raster" tiles={[wmsTiles]}>
      <RanaMapFullscreen.Layer
        id={`publication-raster-layer-${layer.id}`}
        beforeId={beforeId}
        type="raster"
        paint={{ "raster-opacity": layer.opacity }}
        isVisible={layer.visible}
      />
    </RanaMapFullscreen.Source>
  );
}
